'use client';

import Section from '@egalite/ui/IncluiPCD/Section';
import SectionTitle from '@egalite/ui/IncluiPCD/SectionTitle';
import { FC, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { liveStore } from './LiveUpdater';

const CHAT_LEAD_MS = 30 * 60 * 1000;

const Content: FC<{ liveId?: string; startAt?: string | null }> = ({ liveId, startAt }) => {
	const snap = useSnapshot(liveStore);

	// O chat existe da abertura (`startAt` menos CHAT_LEAD_MS) em diante: reprise
	// chega com startAt null e palestra distante ainda não abriu. `hostname` só
	// resolve no cliente (window não existe no render do servidor), então também
	// serve de guarda de hidratação.
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [hostname, setHostname] = useState<string>();

	// O chat do YouTube segue o prefers-color-scheme do visitante, mas deixa o
	// fundo transparente: em tema escuro o texto vira branco sobre o branco do
	// iframe e as mensagens somem. `dark_theme=1` é o único jeito de fazer o
	// YouTube pintar o fundo escuro junto (`dark_theme=0` é ignorado).
	const [darkChat, setDarkChat] = useState(false);

	useEffect(() => {
		liveStore.liveId = liveId;
		liveStore.startAt = startAt;
		setHostname(window.location.hostname);

		const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
		setDarkChat(prefersDark.matches);

		const onSchemeChange = (event: MediaQueryListEvent) => setDarkChat(event.matches);
		prefersDark.addEventListener('change', onSchemeChange);

		return () => prefersDark.removeEventListener('change', onSchemeChange);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const startAtMs = snap.startAt ? Date.parse(snap.startAt) : NaN;

		if (isNaN(startAtMs)) {
			setIsChatOpen(false);
			return;
		}

		const msUntilOpen = startAtMs - CHAT_LEAD_MS - Date.now();

		setIsChatOpen(msUntilOpen <= 0);

		if (msUntilOpen > 0) {
			const id = setTimeout(() => setIsChatOpen(true), msUntilOpen);
			return () => clearTimeout(id);
		}
	}, [snap.startAt]);

	if (!snap.liveId || snap.liveId === '' || snap.liveId === 'TBD' || snap.liveId === 'null') return null;

	const showChat = isChatOpen && hostname != null;

	return (
		<Section id="lives">
			<SectionTitle>Palestras</SectionTitle>

			<div className="w-full flex flex-col gap-4 lg:flex-row lg:items-stretch">
				<iframe
					className="w-full aspect-video lg:flex-[2] lg:min-w-0"
					src={`https://www.youtube.com/embed/${snap.liveId}?autoplay=1&mute=1`}
					title="YouTube video player"
					frameBorder="0"
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
					allowFullScreen
				></iframe>

				{showChat && (
					<iframe
						className="w-full h-[480px] lg:h-auto lg:flex-1 lg:min-w-0"
						src={`https://www.youtube.com/live_chat?v=${snap.liveId}&embed_domain=${hostname}${darkChat ? '&dark_theme=1' : ''}`}
						title="YouTube chat"
						frameBorder="0"
					></iframe>
				)}
			</div>
		</Section>
	);
};

export default Content;
